import { ApprovalProcessConfigViewModel } from '@ail/orders-api'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { downloadFile } from '../../../helpers/FileDownload'
import { useGetTenantsQuery } from '../../../redux/api/customerApi'
import { useGetApprovalProcessConfigsQuery } from '../../../redux/api/ordersApi'
import { Grid, GridColumn } from '../../common/Grid/Grid'
import './WorkflowsList.scss'
import { Loader } from '../../common/Loader/Loader'

interface GridRow extends ApprovalProcessConfigViewModel {
  tenantName?: string
}
export default function WorkflowsListFunctional() {
  const { i18n } = useTranslation('common')
  const lang = i18n.language
  const tenants = useGetTenantsQuery()
  const approvals = useGetApprovalProcessConfigsQuery()

  const downloadJson = useCallback(
    (form: GridRow) => {
      const type = 'application/json'
      const file = new Blob([JSON.stringify(form)], { type })
      const fileURL = URL.createObjectURL(file)
      const filename = `${form.tr?.[lang] || form.name}.json`

      downloadFile({ fileURL, filename })
    },
    [lang]
  )

  const getTenantName = useCallback(
    (ownerId: string) => {
      const tenant = tenants.data?.find((t) => t.id === ownerId)
      return tenant?.name || ''
    },
    [tenants.data]
  )
  // #region FilterFns
  const filterFns = useMemo(() => {
    return {
      byGlobalString: (workflow: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true

        const targetString = [workflow.owner, workflow.tr?.[lang], workflow.name, workflow.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return splitQuery.every((q) => targetString.includes(q))
      },
      byWorkflowId: (workflow: GridRow, value: string) => {
        const val = value.trim().toLowerCase()
        return workflow.id.toLowerCase().includes(val)
      },
      byName: (workflow: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true

        const fullName = `${workflow.name || ''} ${workflow.tr?.[lang] || ''}`.toLowerCase()

        return splitQuery.every((q) => fullName.includes(q))
      },
      byOwner: (workflow: GridRow, value?: string) => {
        if (!value) return true
        return workflow.tenantName?.toLowerCase().includes(value.toLowerCase())
      },
      byOwnerId: (workflow: GridRow, value: string) => {
        const val = value.trim().toLowerCase()
        return workflow.owner.toLowerCase().includes(val)
      },
    }
  }, [lang])
  // #endregion

  // #region DataRows
  const dataRows: GridRow[] = useMemo(() => {
    if (!approvals.data) return []

    return approvals.data.map((r) => {
      const row: GridRow = {
        ...r,
        tenantName: getTenantName(r.owner),
      }
      return row
    })
  }, [approvals.data, getTenantName])
  // #endregion

  // #region Columns
  const columns: GridColumn<GridRow>[] = useMemo(
    () => [
      {
        key: 'id',
        name: 'Workflow ID',
        width: 350,
        cellClass: 'id',
        renderCell: ({ row }) => <div>{row.id}</div>,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byWorkflowId,
          customPlaceholder: 'Search by workflow ID',
        },
      },
      {
        key: 'type',
        name: 'Workflow name',
        width: 350,
        cellClass: 'type',
        renderCell: ({ row }) => <div>{row.tr?.[lang] || row.name}</div>,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byName,
          customPlaceholder: 'Search by workflow name',
        },
      },
      {
        key: 'owner',
        name: 'Owner',
        width: 350,
        cellClass: 'owner',
        renderCell: ({ row }) => <div>{row.tenantName}</div>,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byOwner,
          customPlaceholder: 'Search by owner',
        },
      },
      {
        key: 'workflowOwnerId',
        name: 'Owner ID',
        width: 300,
        cellClass: 'id',
        renderCell: ({ row }) => <div>{row.owner}</div>,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byOwnerId,
          customPlaceholder: 'Search by owner ID',
        },
      },
      {
        key: 'amount',
        name: 'Amount of steps',
        width: 150,
        cellClass: 'amount',
        renderCell: ({ row }) => <div>{row.steps?.length ?? 0}</div>,
        filter: {
          position: 'header-right',
          type: 'inline_text_filter',
          filterFn: filterFns.byGlobalString,
          customPlaceholder: 'Global search',
        },
      },
      {
        key: 'actions',
        name: 'Actions',
        width: 150,
        renderCell: ({ row }) => (
          <div className="actions">
            <NavLink to={`/RequestsManagement/EditWorkflow/${row.id}`}>
              <i className="icon-edit" />
            </NavLink>
            <a onClick={() => downloadJson(row)}>
              <i className="icon-download" />
            </a>
          </div>
        ),
      },
    ],
    [
      filterFns.byWorkflowId,
      filterFns.byName,
      filterFns.byOwner,
      filterFns.byOwnerId,
      filterFns.byGlobalString,
      lang,
      downloadJson,
    ]
  )
  // #endregion

  // #region Render
  return (
    <Loader blocking={approvals.isFetching} className="grid-size">
      <Grid
        id="WorkflowsListAdminPanel"
        columns={columns}
        rows={dataRows}
        rowKeyGetter={(r) => r.id}
        title="Workflow List"
        icon="icon-requests"
        customClass="adminPanelGrid workflowsList"
        renderCustomOption={() => {
          return [
            <NavLink className={'grid-options-widget'} key={'newWorkflow'} to="/RequestsManagement/EditWorkflow/new">
              New Workflow
            </NavLink>,
            <NavLink className={'grid-options-widget'} key={'formsList'} to="/RequestsManagement">
              Requests Management
            </NavLink>,
          ]
        }}
        hideReorder
        hideExport
      />
    </Loader>
  )
}
